import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { formatDateAndTime, formatDuration } from "@/lib/internalApis";
import { SegmentInfo } from "@/types/search/flight";
import { format, parseISO } from "date-fns";
import { PlaneIcon } from "lucide-react";
import FlightLine from "./flight-line";
import Image from "next/image";

export function FlightDetailsDialog({ sI }: any) {


  function getOrdinalSuffix(day: any) {
    if (day > 3 && day < 21) return "th"; // 11th, 12th, 13th...
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  }
  function formatWithOrdinal(date: any) {
    const day = format(date, "d");
    const suffix = getOrdinalSuffix(parseInt(day));
    return format(date, `EEE, MMM d yyyy`).replace(day, day + suffix);
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <span className="text-[12px] font-bold text-green-500 ml-6 mt-2 cursor-pointer">Flight Details</span>
      </DialogTrigger>
      <DialogContent className="max-w-[700px] gap-0">
        <DialogHeader className="mb-2">
          <DialogTitle>Flight Details</DialogTitle>
        </DialogHeader>
        {sI.length > 0 &&
          sI.map((trip: SegmentInfo, index: number) => {
            const { aa, da, stops } = trip;
            const parsedADate = parseISO(trip.at);
            const { formattedTime: deparTime } = formatDateAndTime(trip.dt);
            const { formattedTime: arriveTime } = formatDateAndTime(trip.at);

            const formattedADate = formatWithOrdinal(parsedADate);
            const parsedDDate = parseISO(trip.dt);
            const formattedDDate = formatWithOrdinal(parsedDDate);
            const flightLogo = `/AirlinesLogo/${trip.fD.aI.code}.png`;

            return (
              <>
                <div key={trip.id || index} className={`${index !== 0 && trip.sN === 0 ? "border-t border-gray-200 mt-2" : ""} overflow-y-auto`}>
                <section className="hidden md:block">
                  <div className="flex flex-col md:flex-row items-center justify-between rounded-md shadow-sm">
                    <div className="flex items-center space-x-2">
                      <strong className="text-xs">{da.city}</strong>
                      <span className="text-sm lg:text-xl">→</span>
                      <strong className="text-xs">{aa.city}</strong>
                    </div>
                    <div className="flex items-center text-xs bg-white/20 rounded-md px-4 py-1">
                      {formattedDDate}
                    </div>
                  </div>
                </section>

                <section className="pt-4">
                  <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                    <div className="flex gap-3 w-full md:w-1/5">
                      <div className="relative ">
                        <Image
                          className="w-8 h-8 rounded-full border-2 border-red-100"
                          src={flightLogo}
                          alt={trip.fD.aI.code}
                          width={40}
                          height={40}
                        />
                        <div className="absolute bottom-1 right-0 hidden lg:block bg-primary text-white text-[8px] rounded-full px-[1px] font-bold">
                          {trip.fD.aI.code}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-gray-800 text-xs">
                          {trip.fD.aI.name}
                        </div>
                        <div className="flex items-center justify-center text-[8px] text-gray-600 mt-1">
                          <PlaneIcon className="w-3 h-3 mr-1 text-primary" />
                          {trip.fD.fN}
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 md:px-6 ">
                      <div className="flex justify-between items-start">
                        <div className="text-left space-y-1 w-full md:w-2/5">
                          <div className="text-xs font-bold text-gray-900">
                            {formattedDDate}-{`(${deparTime})`}
                          </div>
                          <div className="text-primary font-medium text-[10px]">
                            {da.city}
                          </div>
                          <div className="text-gray-600 text-[10px] flex flex-col">
                            <span>{da.name}</span>
                            <span>{da.terminal}</span>
                          </div>
                        </div>

                        <div className="flex flex-col items-center w-full md:w-1/5 pt-2">
                          <FlightLine />
                          <div className="mt-4 text-center">
                            <div className="text-[10px] font-medium text-gray-600">
                              {stops === 0 ? "Non-stop" : `${stops} stop(s)`}
                            </div>
                            <div className="text-[10px] font-bold text-primary">
                              {formatDuration(trip.duration)}
                            </div>
                          </div>
                        </div>

                        <div className="text-right space-y-1 w-full md:w-2/5">
                          <div className="text-xs font-bold text-gray-900">
                            {formattedADate}-{`(${arriveTime})`}
                          </div>
                          <div className="text-primary font-medium text-[10px]">
                            {aa.city}
                          </div>
                          <div className=" text-gray-600 text-[10px] flex flex-col">
                            <span>{aa.name}</span>
                            <span>{aa.terminal}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {sI[index]?.cT && (
                    <div className="py-1 px-4 bg-gray-200 border rounded-md text-center  md:w-[60%] m-auto text-gray_dark text-[12px]  my-2 ">
                      Require to change PlaneLayover Time -{" "}
                      {formatDuration(sI[index]?.cT ?? 0)}
                    </div>
                  )}

                </section>
              </div>
              </>
            );
          })}
      </DialogContent>
    </Dialog>
  );
}
